import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './principal-panel.hbs'
import principalItem from './principal-item.hbs'
import principals from './principals'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import MultiPrincipalChooser from 'scoreboard/components/multi-principal-chooser'
import spotlight from 'scoreboard/components/spotlight'

// cache of potential principals that can be chosen.  keep it outside
// individual PrincipalPanel instances.  Note this is cleared out in the
// remove method of the view so navigating to a new screen always clears the
// cache
var potentialPrincipals

var PrincipalPanel = Backbone.View.extend(/** @lends PrincipalPanel.prototype */{

	className: 'panel panel-with-table principal-panel',

	events: {
		'click .delete': 'delete'
	},

	/**
	 * Subclasses can override (see datasets/edit/permissions-panel.js)
	 * see function `_selectTableContent` below
	 */
	_principalTableSelector: 'table',

	/**
	 * Creates a panel used for choosing one or more principals (users or
	 * groups)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {String} options.headingKey
	 * 	For text like "Assigned Users and Groups"
	 *
	 * @param {String} options.placeholderKey
	 * 	For text like "Add Owner..."
	 * 	If not specified, no placeholder row will be added to the table.
	 *
	 * @param {Array} options.currentPrincipals
	 * 	principals already assigned
	 *
	 * @param {Boolean} [options.readOnly=false]
	 * 	Pass true to just render the principals and omit the
	 *     MultiPrincipalChooser. If there are no existing principals the
	 *     placeholderKey will still be rendered.
	 *
	 * @param {String|Function} [options.url='api/principals'] url to fetch principals
	 *
	 * @param {String} [options.sort='label'] pass falsy to disable any sort
	 *
	 * @param {Array} [options.columnHeaders] column headers to add to the table
	 * after the add row.
	 */
	initialize: function(options = {}) {
		this.options = _.extend({
			url: 'api/principals',
			readOnly: false,
			sort: 'label'
		}, options)
		this.url = this.options.url
		this.readOnly = this.options.readOnly
		this.on('change', this.toggleHasPrincipals.bind(this))
	},

	_getTemplate: function() {
		return this.template || template
	},

	render: function() {
		this.adding = false
		this.$el.empty().append(this._getTemplate()(this.options))

		this.renderPrincipals(this.options.currentPrincipals)

		// this seems to be needed since we're building up the DOM largely
		// programmatically
		this.delegateEvents()

		this._setupTooltips()
		this._renderPrincipalChooser()
		this.toggleHasPrincipals()
		this.addColumnHeaders()
		// call resize after all has been rendered to the DOM
		process.nextTick(() => this.resize())
		return this
	},

	_setupTooltips: function() {
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
	},

	/**
	 * Adds or replaces existing principals with those provided.
	 * Leaves the add row intact.
	 */
	renderPrincipals: function (principals) {
		principals = this._principalsAsSet(principals)
		this.currentPrincipals = principals
		var table = this.$('table tbody')
		table.find('tr').not('.add').remove()

		const {sort} = this.options.sort
		const currentPrincipals = sort ? _.sortBy(principals, this.sort) : principals
		_.each(currentPrincipals, p => {
			const principalRow = this.renderPrincipal(p)
			table.append(principalRow)
			this.afterPrincipalAttached(principalRow, p)
		})

		_.invoke(this.principalChooser, 'updatePrincipals', principals)
	},

	/**
	 * #31777 Some permission/filter combination results in duplicate results.
	 * We filter them out so that we don't see duplicate rows in the UI
	 */
	_principalsAsSet: function(principals) {
		return _.uniqBy(principals, p => [p.id, p.type].join('_'))
	},

	// TODO: rename this, it doesn't render anything; 'createPrincipalTr' would be better
	renderPrincipal: function (principal) {
		return principalItem(principals.addViewContextAttributes(principal))
	},

	_renderPrincipalChooser: function() {
		const el = this._selectTableContent('.table-add td')
		if (this.readOnly) {
			// Don't show the placeholder text if there are existing principals
			!_.isEmpty(this.currentPrincipals) && el.remove()
		} else {
			const options = _.extend({
				hideSelectedOptions: true,
				cache: potentialPrincipals
			}, this.options, { el })
			this.principalChooser = new MultiPrincipalChooser(options).render()
			this.principalChooser.on('load', data => potentialPrincipals = data)
			this.principalChooser.on('select', p => this.addPrincipal(p))
		}
	},

	resize() {
		// It when the view resizes, make sure the rollup selects fit well within the
		// cells they're in.
		this.$('tr.principal-item').each((i, pi) => {
			let cell = this.$(pi).find('.principal-name')
			let cellWidth = cell.innerWidth()
			let avatar = cell.find('.avatar')
			let label = cell.find('.principal-label')
			cell.find('.select.permissions-rollup-select .select-control').css('max-width', () => {
				let width = cellWidth - avatar.outerWidth() - label.outerWidth()
				width -= 45 // adjust for padding/margin
				return width
			})
		})
	},

	addPrincipal: function(principal) {
		// Ensures the modelRow is created before any other panels do operations that depend on it
		// 1. Gives permissions-by-filter a chance to decorate the principal
		// 2. Allows rollup selects to be rendered with the correct option selected
		this.trigger('before-add', principal)

		let addRow = this._selectTableContent('tr.add')
		let headerRow = this._selectTableContent('tr.header-row')
		let lastNonPrincipalItemRow = headerRow.length ? headerRow : addRow
		// add the selected item to the table
		lastNonPrincipalItemRow.after(this.renderPrincipal(principal))
		const newRow = addRow.next()
		// process.nextTick() is needed for the spotlight to fade back out and not remain in the yellow state
		process.nextTick(() => spotlight(newRow))
		if (!_.isNil(this.model) &&
			_.get(this.model.attributes, 'type') == 'dataset') {
			// #31445 add text-link appearance to principal name
			// if new principal is in datasets
			this.$('tr:not([data-id=-1]) .principal-label')
				.addClass('text-link')
		}

		this.addColumnHeaders()

		// This call needs to come after any manipulation to the principal row so that it can make
		// any needed changes after the row has been completely built.
		this.afterPrincipalAttached(newRow, principal)
		this.trigger('change change:add', principal, newRow)
		this.resize()
	},

	/**
	 * Adds column headers to the table if they are defined and
	 * haven't been rendered yet.
	 */
	addColumnHeaders: function() {
		let hasPrincipals = this.getPrincipals()
		let hasHeaderRow = this.$('.header-row').length > 0

		if (this.options.columnHeaders && !hasHeaderRow && hasPrincipals) {
			let headers = this.options.columnHeaders.map(header => {
				return `<td>${_.escape(header)}</td>`
			})
			this.$('.principal-item')
				.first()
				.before(`<tr class="header-row">${headers}</tr>`)
			this.columnHeadersRendered = true
		}
	},

	/**
	 * Called after the html returned from `renderPrincipal` is attached to the DOM.
	 * Gives subclasses a chance to do something with the row (data) during rendering.
	 * (e.g. datasets-principal-panel needs to render rollup selects with default values)
	 */
	afterPrincipalAttached: function(principalRow, principal) {},

	setPlaceHolderText(text) {
		this.$('.placeholder').text(text)
	},

	'delete': function(e) {
		if ($(e.target).hasClass('is-disabled')) {
			return
		}

		var tr = $(e.target).closest('tr[data-id]')
		const row = this.getPrincipalDataRow(tr)
		const principal = row.data()
		_.invoke(this.principalChooser, 'removePrincipal', principal)
		tr.remove()
		this.trigger('change change:delete', principal, row)
		this.resize()

		if (this.getPrincipals().length === 0) {
			this.$('.header-row').remove()
			this.columnHeadersRendered = false
		}
		return principal
	},

	/**
	 * Given a table row, returns the row containing data associated to it.
	 * Which should include id and type properties at a minimum.
	 *
	 * For a basic principal panel this is the row that was clicked.  More complex
	 * components may override this function.
	 *
	 * @param {jQuery} tr
	 * @returns {jQuery} row
	 */
	getPrincipalDataRow: function(tr) {
		return tr
	},

	// returns the important information about the principals rendered in this
	// panel (ID, type and updateThresholds).  The label isn't important for
	// sending to the server so it isn't returned
	getPrincipals: function() {
		return _.map(this._selectTableContent('[data-id]'), row => {
			row = $(row)
			var principal = {
				id: row.data('id'),
				type: row.data('type'),
				label: row.data('label'),
				groupType: row.data('groupType'),
				// #50380 populate `fromTemplate` property, default to false
				fromTemplate: row.data('fromTemplate') || false
			}
			this.readPrincipalOptionsFromDomRow(row, principal)

			return principal
		})
	},

	/**
	 * Subclasses may add additional form elements to the rows
	 * (e.g. 'update thresholds' for scorecards, dataset rollup tree options, etc)
	 * @param {jQuery} the principal row (<tr>)
	 * @param {Principal} principal
	 */
	readPrincipalOptionsFromDomRow: function (row, principal) {},

	/**
	 * @return {jQuery} the principal rows
	 */
	getRows: function() {
		return this._selectTableContent('tbody tr')
	},

	// check to see if there are principals, if so, mark it with .has-principals
	toggleHasPrincipals: function() {
		var principals = this._selectTableContent('.principal-item').length
		this.$el.toggleClass('has-principals', principals > 0)
	},

	/**
	 * If a view that extends this adds other tables, it can provide a more
	 * specific selector so that only the principal table is searched.  This
	 * function must be used any time the table is being searched for an element
	 *
	 * @param {String} selector
	 */
	_selectTableContent: function(selector) {
		return this.$(`${this._principalTableSelector} ${selector}`)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this.principalChooser], 'remove')
		potentialPrincipals = null
	}

})

export default PrincipalPanel

PrincipalPanel.__filename__ = 'components/principal-panel.js'
